import React from 'react';
import { Tooltip, Typography } from 'antd';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import {
  LocationMapSvg,
  CheckMarkSvg,
  TrustYouSVG,
  InfoSvg,
  ArrowBottomSvg,
  ArrowTopSvg,
  NoImageSvg,
} from 'src/assets/index';
import { IFullPublicPrice, IHotel, ISupplier, RefundableEnum } from 'src/common-types/index';
import { renderNumber, getTopSuppliers, openHotelPage, getFullAddress } from 'src/utils/index';
import { HotelStars } from 'src/components/components/index';
import { CarouselHotelImages } from '../carousel-hotel-images';
import './style.scss';

interface IProps extends RouteComponentProps {
  hotel: IHotel;
  nights: number;
  suppliers: ISupplier[];
  matches?: boolean;
  isFromSearch?: boolean;
  showOnMap?: () => void;
  disabled?: boolean;
}

interface IState {
  isAllSuppliersShown: boolean;
}

const zero = 0;
const percents = 100;
const amenitiesMaxRows = 2;
const maxCountOfSimpleSuppliers = 3;
const pricesPaddings = 30;
const priceWidth = 164;

class HotelCardWrapper extends React.Component<IProps, IState> {
  cardRef: React.RefObject<HTMLDivElement> = React.createRef();
  state = {
    isAllSuppliersShown: false,
  };

  getMinPublicPrice = (): number => {
    const { publicPrices } = this.props.hotel;

    if (publicPrices && publicPrices.length) {
      return Math.min(...publicPrices.map(({ price }) => price));
    }

    return zero;
  };

  toggleSuppliers = () => {
    this.setState(({ isAllSuppliersShown }) => ({
      isAllSuppliersShown: !isAllSuppliersShown,
    }));
  };

  getMaxPrices = (): number => {
    if (this.cardRef && this.cardRef.current) {
      return Math.floor((this.cardRef.current.offsetWidth - pricesPaddings) / priceWidth);
    }

    return zero;
  };

  getPricesComponent = (
    sortedSuppliers: IFullPublicPrice[],
    isMobile = false,
  ): React.ReactElement => {
    const { isAllSuppliersShown } = this.state;

    return (
      <div className={`hotel-card-wrapper__suppliers ${isMobile ? 'mobile' : 'desktop'}`}>
        <div className="hotel-card-wrapper__suppliers-list">
          {sortedSuppliers
            .slice(zero, maxCountOfSimpleSuppliers)
            .map(({ supplierId, supplier, price }) => {
              return (
                <div className="hotel-card-wrapper__simple-supplier" key={supplierId}>
                  <div className="hotel-card-wrapper__simple-supplier-name">
                    {supplier.displayName}
                  </div>
                  <div className="hotel-card-wrapper__simple-supplier-price">
                    ${renderNumber(Math.floor(price))}
                  </div>
                </div>
              );
            })}
        </div>
        {sortedSuppliers.length > zero ? (
          <div className="hotel-card-wrapper__show-more-suppliers" onClick={this.toggleSuppliers}>
            {isAllSuppliersShown ? (
              <>
                <FormattedMessage id="hide" />
                <ArrowTopSvg />
              </>
            ) : (
              <>
                <FormattedMessage id="more.public.pricing" />
                <ArrowBottomSvg />
              </>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  onHotelPage = () => {
    const { hotelId } = this.props.hotel;

    openHotelPage(hotelId, this.props.history);
  };

  onMapPage = () => {
    if (this.props.showOnMap) {
      this.props.showOnMap();
    }
  };

  render(): React.ReactNode {
    const { isAllSuppliersShown } = this.state;
    const { hotel, nights, suppliers, matches, isFromSearch = true, disabled = false } = this.props;
    const {
      address,
      displayName,
      images,
      price,
      rating,
      pricePerNight,
      amenities,
      publicPrices,
      starRating,
      city,
      countryCode,
      zipCode,
      state,
      refundability,
      savings,
    } = hotel;
    const pubPrice = this.getMinPublicPrice();
    const save = (price * percents) / pubPrice;
    const savePercents = Math.floor(percents - save);
    const sortedSuppliers = getTopSuppliers(publicPrices || [], suppliers);
    const fullAddress = getFullAddress(address, city, state, zipCode, countryCode);
    const addressStr = fullAddress.join(', ');

    return (
      <div className={`hotel-card ${disabled ? 'disabled' : ''}`}>
        {disabled && <div className="hotel-card__disabled-wrapper" />}
        <div
          className={`hotel-card-wrapper
          ${isAllSuppliersShown ? 'prices-open' : ''}`}
          ref={this.cardRef}
        >
          <div className="hotel-card-wrapper__slider">
            {!!isFinite(savePercents) && !disabled && (
              <div className="hotel-card-wrapper__label">
                <p className="hotel-card-wrapper__label-title">
                  <FormattedMessage id="save" /> {savePercents}%
                </p>
              </div>
            )}
            {images?.length ? (
              <CarouselHotelImages images={images} />
            ) : (
              <div className="hotel-card-wrapper__no-image">
                <NoImageSvg />
              </div>
            )}
          </div>
          <div className="hotel-card-wrapper__content">
            <div className="hotel-card-wrapper__hotel-info">
              <div className="hotel-card-wrapper__hotel-info-items">
                <div className="hotel-card-wrapper__hotel-info-item">
                  {isFromSearch ? (
                    <>
                      <h4
                        className="hotel-card-wrapper__hotel-info-title header-title"
                        onClick={this.onHotelPage}
                      >
                        {displayName}{' '}
                      </h4>
                      <h4 className="hotel-card-wrapper__hotel-info-title">
                        <HotelStars stars={starRating} />
                      </h4>
                    </>
                  ) : (
                    <h4 className="hotel-card-wrapper__hotel-info-title">
                      {displayName} <HotelStars stars={starRating} />
                    </h4>
                  )}
                  <div className="hotel-card-wrapper__hotel-info-address">
                    <p className="hotel-card-wrapper__address">
                      {fullAddress.length !== zero ? (
                        <Tooltip title={addressStr}>
                          <span>{addressStr}</span>
                        </Tooltip>
                      ) : (
                        address
                      )}
                    </p>
                    {isFromSearch && (
                      <a onClick={this.onMapPage} className="hotel-card-wrapper__link-map">
                        <LocationMapSvg />
                        <span className="hotel-card-wrapper__link-text">
                          <FormattedMessage id="view.map" />
                        </span>
                      </a>
                    )}
                  </div>
                  {!isFromSearch && (
                    <div className="hotel-card-wrapper__map-mobile-data">
                      <div className="hotel-card-wrapper__mobile-price-per-night">
                        <FormattedMessage id="from" />{' '}
                        <span style={{ fontWeight: 'bold' }}>
                          ${renderNumber(pricePerNight)}/<FormattedMessage id="per.night" />
                        </span>
                      </div>
                      <div className="hotel-card-wrapper__mobile-total">
                        <span className="hotel-card-wrapper__mobile-total-price">
                          ${renderNumber(Math.floor(price))}{' '}
                        </span>
                        <FormattedMessage id="for" />{' '}
                        <FormattedMessage id="nights" values={{ count: nights }} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="hotel-card-wrapper__label-info">
                  <p className="hotel-card-wrapper__label-text">
                    <FormattedMessage id="from" />
                  </p>
                  <p className="hotel-card-wrapper__label-price">${renderNumber(pricePerNight)}</p>
                  <p className="hotel-card-wrapper__label-text">
                    <FormattedMessage id="per.night" />
                  </p>
                </div>
              </div>
              {isFromSearch && (
                <div className="hotel-card-wrapper__field">
                  <div className="hotel-card-wrapper__icon-check">
                    <CheckMarkSvg />
                  </div>
                  <div className="hotel-card-wrapper__description">
                    <Typography.Paragraph
                      ellipsis={{
                        rows: amenitiesMaxRows,
                      }}
                    >
                      {amenities.join(', ')}
                    </Typography.Paragraph>
                  </div>
                </div>
              )}
              {rating && (
                <div
                  className={`hotel-card-wrapper__trust-you-info ${
                    rating.value === zero ? 'hide-trust-you-info' : ''
                  }`}
                >
                  <div className="hotel-card-wrapper__trust-you-icon">
                    <TrustYouSVG />
                  </div>
                  <p className="hotel-card-wrapper__trust-you-text">
                    <span className="hotel-card-wrapper__bold">{rating.value}</span>(
                    {renderNumber(rating.reviewsCount)} <FormattedMessage id="reviews" />)
                  </p>
                </div>
              )}
            </div>
            {sortedSuppliers && sortedSuppliers.length && isFromSearch
              ? this.getPricesComponent(sortedSuppliers)
              : null}
          </div>
          <div className="hotel-card-wrapper__price-content">
            <div className="hotel-card-wrapper__price-content-top">
              {pubPrice > zero || savings > zero || !matches ? (
                <div className="hotel-card-wrapper__price-info">
                  {pubPrice > zero && (
                    <div className="hotel-card-wrapper__price-public">
                      <p className="hotel-card-wrapper__public-text">
                        <FormattedMessage id="public.price" />
                      </p>
                      <p className="hotel-card-wrapper__public-text">
                        ${renderNumber(Math.floor(pubPrice))}
                      </p>
                    </div>
                  )}
                  {savings > zero && (
                    <div className="hotel-card-wrapper__price-savings">
                      <p className="hotel-card-wrapper__savings-text">
                        <FormattedMessage id="savings" />
                      </p>
                      <p className="hotel-card-wrapper__savings-text">${renderNumber(savings)}</p>
                    </div>
                  )}
                </div>
              ) : null}
              <div className="hotel-card-wrapper__price-total">
                {refundability && refundability !== RefundableEnum.Unknown ? (
                  <div className="hotel-card-wrapper__refundable">
                    {refundability === RefundableEnum.Refundable && (
                      <div className="hotel-card-wrapper__refundable-ref">
                        <FormattedMessage id="refundable" />
                      </div>
                    )}
                    {refundability === RefundableEnum.Nonrefundable && (
                      <div className="hotel-card-wrapper__refundable-nonref">
                        <FormattedMessage id="non.refundable" />
                      </div>
                    )}
                  </div>
                ) : null}
                <p className="hotel-card-wrapper__price-total-text">
                  ${renderNumber(Math.floor(price))}
                </p>
                <div className="hotel-card-wrapper__for-info">
                  <p className="hotel-card-wrapper__for-info-text">
                    <FormattedMessage id="for" />{' '}
                    <FormattedMessage id="nights" values={{ count: nights }} />
                  </p>
                  <Tooltip title={<FormattedMessage id="price.includes.taxes.fees" />}>
                    <span className="hotel-card-wrapper__price-info-icon">
                      <InfoSvg />
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
            {isFromSearch && (
              <div className="hotel-card-wrapper__price-content-bottom" onClick={this.onHotelPage}>
                <a className="hotel-card-wrapper__price-btn">
                  <FormattedMessage id="select.room" />
                </a>
              </div>
            )}
          </div>
          {sortedSuppliers && sortedSuppliers.length && isFromSearch
            ? this.getPricesComponent(sortedSuppliers, true)
            : null}
        </div>
        {isAllSuppliersShown && isFromSearch ? (
          <div className="hotel-card-wrapper__public-prices">
            {(matches ? sortedSuppliers.slice(zero, this.getMaxPrices()) : sortedSuppliers).map(
              ({ price, supplier, supplierId }) => {
                return (
                  <div className="hotel-card-wrapper__public-price-item" key={supplierId}>
                    <div className="hotel-card-wrapper__supplier-logo-wrapper">
                      <img className="hotel-card-wrapper__supplier-img" src={supplier.imageUrl} />
                    </div>
                    <div className="hotel-card-wrapper__public-price-item-right">
                      <div className="hotel-card-wrapper__full-supplier-price">
                        ${renderNumber(Math.floor(price))}
                      </div>
                      <div className="hotel-card-wrapper__full-supplier-nights">
                        <div className="hotel-card-wrapper__for-info">
                          <p className="hotel-card-wrapper__for-info-text">
                            <FormattedMessage id="for" />{' '}
                            <FormattedMessage id="nights" values={{ count: nights }} />
                          </p>
                          <Tooltip title={<FormattedMessage id="price.includes.taxes.fees" />}>
                            <span className="hotel-card-wrapper__price-info-icon">
                              <InfoSvg />
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              },
            )}
          </div>
        ) : null}
      </div>
    );
  }
}

export const HotelCardComponent = withRouter(HotelCardWrapper);
